import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Sunrise, Coffee, Sunset, Check, Loader2, RefreshCw, Users, KeyRound, Delete, Trophy } from "lucide-react";
import { toast } from "sonner";

// Big shared "kiosk-style" board — every stylist working today can see
// who owns which opening / downtime / closing task. Anyone signed in for
// the tenant can view it; taps mark a task complete (server records who).
// Auto-refreshes every 20 seconds so newly-clocked-in staff show up.

const SECTIONS = [
  { key: "opening",  label: "Opening",  Icon: Sunrise, accent: "from-amber-500 to-orange-500", accentSoft: "bg-amber-50 text-amber-700" },
  { key: "downtime", label: "Downtime", Icon: Coffee,  accent: "from-teal-500 to-emerald-500", accentSoft: "bg-emerald-50 text-emerald-700" },
  { key: "closing",  label: "Closing",  Icon: Sunset,  accent: "from-indigo-500 to-violet-500", accentSoft: "bg-violet-50 text-violet-700" },
];

const staffColor = (name) => {
  if (!name) return "bg-stone-300";
  // Deterministic hue from the name so each stylist gets a stable chip colour.
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffff;
  const palette = ["bg-rose-500", "bg-amber-500", "bg-emerald-500", "bg-teal-500",
                   "bg-sky-500", "bg-indigo-500", "bg-violet-500", "bg-fuchsia-500"];
  return palette[h % palette.length];
};

export default function TasksBoardPage() {
  const [board, setBoard] = useState({ workers: [], opening: [], downtime: [], closing: [] });
  const [loading, setLoading] = useState(true);
  const [pinTask, setPinTask] = useState(null); // task pending PIN entry
  // Which section is on screen. "all" shows all three side-by-side, one of
  // the section keys shows just that list full-width (nice for a single-tab
  // mobile / kiosk view).
  const [filter, setFilter] = useState("all");
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";
  const [fairness, setFairness] = useState([]);
  const [skipAlerts, setSkipAlerts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [cap, setCap] = useState(0);
  const [savingCap, setSavingCap] = useState(false);

  const loadManagerInsights = useCallback(async () => {
    try {
      const lb = await api.get("/tasks/leaderboard?days=7");
      setLeaderboard(lb.data?.leaderboard || []);
    } catch { /* non-blocking */ }
    if (!isManager) return;
    try {
      const [f, s, c] = await Promise.all([
        api.get("/tasks/fairness?days=7"),
        api.get("/tasks/skip-alerts?days=7"),
        api.get("/tasks/config/rotation"),
      ]);
      setFairness(f.data?.tally || []);
      setSkipAlerts(s.data || []);
      setCap(c.data?.max_per_person || 0);
    } catch { /* non-blocking */ }
  }, [isManager]);

  const saveCap = async (value) => {
    const v = Math.max(0, Math.min(100, parseInt(value, 10) || 0));
    setCap(v);
    setSavingCap(true);
    try {
      await api.put("/tasks/config/rotation", { max_per_person: v });
      toast.success(v === 0 ? "Cap removed — unlimited chores" : `Capped at ${v} chore${v !== 1 ? "s" : ""} per person`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save cap");
    }
    setSavingCap(false);
  };

  const resolveAlert = async (alertId) => {
    try {
      await api.post(`/tasks/skip-alerts/${alertId}/resolve`);
      setSkipAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      toast.success("Alert cleared");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't clear"); }
  };

  const [redistributing, setRedistributing] = useState(false);
  const redistribute = async () => {
    setRedistributing(true);
    try {
      const res = await api.post("/checklists/rebalance");
      toast.success(`Re-split ${res.data?.assigned ?? 0} chores across ${res.data?.workers ?? 0} staff`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't redistribute");
    }
    setRedistributing(false);
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/tasks/board");
      setBoard(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load board");
    }
    setLoading(false);
    loadManagerInsights();
  }, [loadManagerInsights]);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);   // gentle auto-refresh
    return () => clearInterval(id);
  }, [load]);

  const submitPin = async (pin) => {
    if (!pinTask) return { ok: false };
    try {
      const undo = pinTask.status === "done";
      const res = await api.post(`/tasks/${pinTask.task_id}/complete-with-pin`, { pin, undo });
      toast.success(undo ? `Un-checked by ${res.data.verified_by}` : `Marked done by ${res.data.verified_by}`);
      await load();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || "Couldn't update" };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f4ee]">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }

  const doneCount = SECTIONS.reduce((s, sec) => s + board[sec.key].filter(t => t.status === "done").length, 0);
  const totalCount = SECTIONS.reduce((s, sec) => s + board[sec.key].length, 0);
  const pct = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f7f4ee] via-[#faf7f1] to-[#eef4f2] p-6 lg:p-8" data-testid="tasks-board-page">
      <div className="max-w-[1600px] mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 font-['Manrope']">Today · {board.date}</p>
            <h1 className="font-['Outfit'] text-4xl lg:text-5xl font-light text-[#072032] tracking-tight mt-1">Tasks Board</h1>
            <p className="text-sm text-stone-600 mt-1 font-['Manrope']">
              Auto-split across everyone scheduled today. Tap a task to check it off.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="bg-white border border-stone-200 rounded-2xl px-4 py-3 flex items-center gap-3 min-w-[200px]" data-testid="board-progress">
              <div className="relative w-11 h-11 rounded-full bg-stone-100 flex items-center justify-center">
                <svg className="absolute inset-0" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#e7e5e4" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#10b981" strokeWidth="3"
                    strokeDasharray={`${pct}, 100`} strokeLinecap="round" transform="rotate(-90 18 18)" />
                </svg>
                <span className="text-[11px] font-bold text-[#072032] tabular-nums">{pct}%</span>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 font-['Manrope']">Progress</p>
                <p className="font-['Outfit'] text-lg font-semibold text-[#072032]">{doneCount} / {totalCount}</p>
              </div>
            </div>
            <Button variant="outline" onClick={load} data-testid="board-refresh"
              className="border-stone-200 rounded-full h-11 px-4 text-sm">
              <RefreshCw className="w-4 h-4 mr-1.5" />Refresh
            </Button>
            {isManager && (
              <Button onClick={redistribute} disabled={redistributing} data-testid="board-redistribute"
                className="bg-[#072032] hover:bg-[#0a2c45] text-white rounded-full h-11 px-4 text-sm">
                {redistributing
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <Users className="w-4 h-4 mr-1.5" />}
                Redistribute evenly
              </Button>
            )}
          </div>
        </div>

        {/* Clocked-in staff */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="board-workers">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-stone-400" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500 font-['Manrope']">
                On the clock — {board.workers.length}
              </p>
            </div>
            {isManager && (
              <div className="flex items-center gap-2" data-testid="cap-control">
                <label htmlFor="chore-cap" className="text-[10px] font-bold uppercase tracking-widest text-stone-500 font-['Manrope']">
                  Max chores / person
                </label>
                <input id="chore-cap" type="number" min="0" max="100" value={cap}
                  disabled={savingCap}
                  onChange={(e) => setCap(e.target.value)}
                  onBlur={(e) => saveCap(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  data-testid="cap-input"
                  className="w-16 h-9 rounded-lg border border-stone-200 px-2 text-sm text-[#072032] font-semibold text-center focus:outline-none focus:ring-2 focus:ring-[#1b6f6f]/30" />
                <span className="text-[11px] text-stone-400 font-['Manrope']">{cap === 0 || cap === "0" ? "no limit" : "cap on"}</span>
              </div>
            )}
          </div>
          {board.workers.length === 0 ? (
            <p className="text-sm text-stone-500 font-['Manrope']">
              No one&apos;s clocked in yet. Tasks stay unassigned until the first person clocks in.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {board.workers.map(w => (
                <div key={w.user_id}
                  data-testid={`board-worker-${w.user_id}`}
                  className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-stone-50 border border-stone-200">
                  <span className={`w-7 h-7 rounded-full ${staffColor(w.name)} text-white text-xs font-bold flex items-center justify-center`}>
                    {(w.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="text-sm font-semibold text-[#072032] font-['Outfit']">{w.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Manager insights — fairness tally + skip alerts (managers only) */}
        {isManager && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="manager-insights">
            <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="fairness-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-['Outfit'] text-sm font-bold uppercase tracking-widest text-[#072032]">Chore spread · this week</h3>
                <span className="text-[11px] text-stone-400 font-['Manrope']">by effort</span>
              </div>
              {fairness.length === 0 ? (
                <p className="text-sm text-stone-400 font-['Manrope']">No completed chores yet this week.</p>
              ) : (() => {
                const max = Math.max(...fairness.map(f => f.weight), 1);
                return (
                  <div className="space-y-2">
                    {fairness.map(f => (
                      <div key={f.user_id} className="flex items-center gap-3" data-testid={`fairness-row-${f.user_id}`}>
                        <span className="text-sm font-semibold text-[#072032] font-['Outfit'] w-28 truncate">{f.name || "—"}</span>
                        <div className="flex-1 h-5 rounded-full bg-stone-100 overflow-hidden">
                          {f.weight > 0 && <div className="h-full rounded-full bg-gradient-to-r from-[#0a2b40] to-[#1b6f6f]" style={{ width: `${Math.max(6, (f.weight / max) * 100)}%` }} />}
                        </div>
                        <span className="text-xs text-stone-500 font-['Manrope'] w-24 text-right tabular-nums">{f.tasks} task{f.tasks !== 1 ? "s" : ""} · {f.weight} effort</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="skip-alerts-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-['Outfit'] text-sm font-bold uppercase tracking-widest text-[#072032]">Skipped at clock-out</h3>
                {skipAlerts.length > 0 && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{skipAlerts.filter(a => !a.resolved).length} open</span>}
              </div>
              {skipAlerts.length === 0 ? (
                <p className="text-sm text-stone-400 font-['Manrope']">Nobody has clocked out with unfinished tasks. Nice.</p>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {skipAlerts.map(a => (
                    <div key={a.alert_id} data-testid={`skip-alert-${a.alert_id}`}
                      className={`flex items-start gap-3 p-3 rounded-xl border ${a.resolved ? "border-stone-200 bg-stone-50 opacity-60" : "border-rose-200 bg-rose-50"}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#072032] font-['Outfit']">{a.user_name || "Someone"} <span className="text-stone-400 font-normal">· {a.date}</span></p>
                        <p className="text-xs text-stone-600 font-['Manrope'] mt-0.5">Left {a.count} unfinished: {(a.tasks || []).map(t => t.title).slice(0, 4).join(", ")}{(a.tasks || []).length > 4 ? "…" : ""}</p>
                      </div>
                      {!a.resolved && (
                        <button onClick={() => resolveAlert(a.alert_id)} data-testid={`skip-resolve-${a.alert_id}`}
                          className="text-xs font-semibold text-rose-700 hover:text-rose-900 whitespace-nowrap px-2 py-1 rounded-full hover:bg-rose-100">Clear</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Chore leaderboard — visible to everyone, a little friendly competition */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="leaderboard-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              <h3 className="font-['Outfit'] text-sm font-bold uppercase tracking-widest text-[#072032]">Chore champions · this week</h3>
            </div>
            <span className="text-[11px] text-stone-400 font-['Manrope']">most completed</span>
          </div>
          {leaderboard.length === 0 ? (
            <p className="text-sm text-stone-400 font-['Manrope']">No chores completed yet this week — be the first to top the board!</p>
          ) : (
            <div className="flex flex-wrap gap-3" data-testid="leaderboard-list">
              {leaderboard.slice(0, 8).map(row => {
                const medal = row.rank === 1 ? "bg-amber-100 text-amber-700 border-amber-300"
                  : row.rank === 2 ? "bg-stone-100 text-stone-600 border-stone-300"
                  : row.rank === 3 ? "bg-orange-100 text-orange-700 border-orange-300"
                  : "bg-white text-stone-500 border-stone-200";
                return (
                  <div key={row.user_id} data-testid={`leaderboard-row-${row.user_id}`}
                    className={`flex items-center gap-3 pl-2 pr-4 py-2 rounded-2xl border ${medal}`}>
                    <span className="w-7 h-7 rounded-full bg-white/70 border border-current/20 text-sm font-bold flex items-center justify-center tabular-nums">
                      {row.rank}
                    </span>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold text-[#072032] font-['Outfit']">{row.name || "—"}</p>
                      <p className="text-[11px] text-stone-500 font-['Manrope'] tabular-nums">{row.completed} done · {row.effort} effort</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Section filter */}
        <div className="flex flex-wrap items-center gap-2" data-testid="board-filter">
          {[
            { key: "all",      label: "All lists", count: board.opening.length + board.downtime.length + board.closing.length },
            ...SECTIONS.map(s => ({ key: s.key, label: s.label, count: (board[s.key] || []).length, Icon: s.Icon })),
          ].map(({ key, label, count, Icon }) => {
            const on = filter === key;
            return (
              <button key={key} onClick={() => setFilter(key)}
                data-testid={`board-filter-${key}`}
                className={`h-10 pl-4 pr-3 rounded-full text-sm font-semibold flex items-center gap-2 border transition-all ${
                  on
                    ? "bg-[#072032] text-white border-[#072032] shadow-sm"
                    : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"
                }`}>
                {Icon ? <Icon className="w-4 h-4" strokeWidth={1.6} /> : null}
                {label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  on ? "bg-white/15 text-white" : "bg-stone-100 text-stone-500"
                }`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Sections */}
        <div className={`grid grid-cols-1 gap-5 ${filter === "all" ? "lg:grid-cols-3" : ""}`}>
          {SECTIONS.filter(s => filter === "all" || filter === s.key).map(({ key, label, Icon, accent, accentSoft }) => {
            const tasks = board[key] || [];
            const done = tasks.filter(t => t.status === "done").length;
            return (
              <section key={key} data-testid={`board-section-${key}`}
                className="bg-white border border-stone-200 rounded-3xl overflow-hidden flex flex-col min-h-[420px]">
                <header className={`bg-gradient-to-r ${accent} p-5 text-white`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5" strokeWidth={1.6} />
                      <h2 className="font-['Outfit'] text-xl font-semibold tracking-tight">{label}</h2>
                    </div>
                    <span className="text-xs font-bold bg-white/20 backdrop-blur px-2.5 py-1 rounded-full">
                      {done} / {tasks.length}
                    </span>
                  </div>
                </header>
                <div className="flex-1 p-3 space-y-2 overflow-y-auto">
                  {tasks.length === 0 ? (
                    <div className="p-8 text-center text-sm text-stone-400 font-['Manrope']">
                      No {label.toLowerCase()} tasks configured yet.
                    </div>
                  ) : tasks.map(t => {
                    const isDone = t.status === "done";
                    return (
                      <button key={t.task_id} onClick={() => setPinTask(t)}
                        data-testid={`board-task-${t.task_id}`}
                        className={`w-full text-left rounded-2xl p-3 border transition-all group ${
                          isDone
                            ? "bg-emerald-50/40 border-emerald-100 opacity-75"
                            : "bg-white border-stone-200 hover:border-stone-300 hover:shadow-sm"
                        }`}>
                        <div className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all mt-0.5 ${
                            isDone
                              ? "bg-emerald-500 border-emerald-500"
                              : "border-stone-300 group-hover:border-emerald-400"
                          }`}>
                            {isDone ? <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} /> : null}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold font-['Outfit'] ${isDone ? "text-stone-500 line-through" : "text-[#072032]"}`}>
                              {t.title}
                            </p>
                            {t.description && (
                              <p className="text-xs text-stone-500 mt-0.5 line-clamp-2 font-['Manrope']">{t.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1.5">
                              {t.weight >= 2 ? (
                                <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${t.weight === 3 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`} data-testid={`board-task-${t.task_id}-weight`}>
                                  {t.weight === 3 ? "Heavy" : "Medium"}
                                </span>
                              ) : null}
                              {t.assignee_name ? (
                                <span data-testid={`board-task-${t.task_id}-assignee`}
                                  className="inline-flex items-center gap-1 text-[11px] font-semibold">
                                  <span className={`w-4 h-4 rounded-full ${staffColor(t.assignee_name)} text-white text-[9px] font-bold flex items-center justify-center`}>
                                    {t.assignee_name.slice(0, 1).toUpperCase()}
                                  </span>
                                  <span className="text-stone-700">{t.assignee_name}</span>
                                </span>
                              ) : (
                                <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${accentSoft}`}>
                                  Up for grabs
                                </span>
                              )}
                              {t.estimated_minutes ? (
                                <span className="text-[10px] text-stone-400 font-['Manrope']">~{t.estimated_minutes} min</span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>
      {pinTask && (
        <PinPad
          task={pinTask}
          onClose={() => setPinTask(null)}
          onSubmit={submitPin}
        />
      )}
    </div>
  );
}

function PinPad({ task, onClose, onSubmit }) {
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const submittingRef = useRef(false);
  const isUndo = task.status === "done";

  const doSubmit = useCallback(async (val) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError("");
    const res = await onSubmit(val);
    submittingRef.current = false;
    setSubmitting(false);
    if (res.ok) {
      onClose();
    } else {
      setError(res.error || "Incorrect PIN");
      setPin("");
    }
  }, [onSubmit, onClose]);

  const press = (d) => {
    if (submitting || pin.length >= 6) return;
    const next = pin + String(d);
    setPin(next);
    setError("");
    if (next.length === 4) {
      // Auto-submit at 4 digits — matches the time clock behaviour.
      doSubmit(next);
    }
  };
  const back = () => { if (!submitting) { setPin(pin.slice(0, -1)); setError(""); } };
  const clear = () => { if (!submitting) { setPin(""); setError(""); } };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DialogContent className="max-w-sm bg-[#072032] text-white border-0" data-testid="task-pin-modal">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-white flex items-center gap-2">
            <KeyRound className="w-4 h-4" />
            {isUndo ? "Un-check task" : "Complete task"}
          </DialogTitle>
        </DialogHeader>
        <div className="text-center">
          <p className="text-sm text-white/70 font-['Manrope'] mb-1">Enter your 4-digit PIN to confirm</p>
          <p className="font-['Outfit'] text-lg font-semibold text-white mb-4 line-clamp-2 px-2" data-testid="task-pin-title">
            {task.title}
          </p>
          {/* PIN dots */}
          <div className="flex items-center justify-center gap-3 mb-4">
            {[0, 1, 2, 3].map(i => (
              <span key={i}
                data-testid={`pin-dot-${i}`}
                className={`w-3.5 h-3.5 rounded-full border transition-all ${
                  i < pin.length ? "bg-emerald-400 border-emerald-400 scale-110" : "border-white/30"
                }`} />
            ))}
          </div>
          {error && (
            <p className="text-rose-300 text-xs font-semibold font-['Manrope'] mb-3" data-testid="pin-error">{error}</p>
          )}
          {/* Keypad */}
          <div className="grid grid-cols-3 gap-2.5" data-testid="pin-keypad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
              <button key={d} onClick={() => press(d)} disabled={submitting}
                data-testid={`pin-key-${d}`}
                className="h-14 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-2xl font-['Outfit'] text-white transition-all active:scale-95 disabled:opacity-50">
                {d}
              </button>
            ))}
            <button onClick={clear} disabled={submitting}
              data-testid="pin-key-clear"
              className="h-14 rounded-2xl text-white/60 hover:text-white text-xs font-bold uppercase tracking-widest transition-colors">
              Clear
            </button>
            <button onClick={() => press(0)} disabled={submitting}
              data-testid="pin-key-0"
              className="h-14 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-2xl font-['Outfit'] text-white transition-all active:scale-95 disabled:opacity-50">
              0
            </button>
            <button onClick={back} disabled={submitting}
              data-testid="pin-key-back"
              className="h-14 rounded-2xl text-white/60 hover:text-white flex items-center justify-center transition-colors">
              <Delete className="w-5 h-5" />
            </button>
          </div>
          {submitting && (
            <p className="text-white/60 text-xs mt-3 flex items-center justify-center gap-2 font-['Manrope']">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Verifying…
            </p>
          )}
          <button onClick={onClose} disabled={submitting}
            data-testid="pin-cancel"
            className="mt-4 text-xs text-white/50 hover:text-white/80 font-semibold font-['Manrope'] uppercase tracking-widest">
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
